/**
 * Asta Cafe gallery carousel — the one stateful piece of the landing (the rest is
 * static copy). Renders the 8 ingredient infographics one at a time in a 3:2
 * viewport: autoplay every 6s, pause on hover, prev/next + dots, and an
 * "Open full image" link (why the PNGs need a stable public URL).
 *
 * prefers-reduced-motion is honored in BOTH layers: this component skips autoplay
 * and drops the slide transition, and `asta.css` also `!important`-disables the
 * track transition. Decorative motion "safe to drop" per the handoff.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import { FONT_DISPLAY } from './tokens';

/** Slides come from the site-content manifest via the landing (src resolved). */
export type GallerySlide = { src: string; tag: string; title: string };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function GalleryCarousel({
  slides,
  fit = 'cover',
}: {
  slides: GallerySlide[];
  /** 'contain' for product cutouts that must show whole; 'cover' for infographics. */
  fit?: 'cover' | 'contain';
}): JSX.Element | null {
  const n = slides.length;
  const [slide, setSlide] = useState(0);
  const [reduced] = useState(prefersReducedMotion);
  const pausedRef = useRef(false);

  const goTo = (i: number) => setSlide(i);
  const next = () => setSlide((s) => (s + 1) % n);
  const prev = () => setSlide((s) => (s - 1 + n) % n);

  // Autoplay — 6s, paused while hovering. Skipped entirely under reduced motion.
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      if (!pausedRef.current) setSlide((s) => (s + 1) % n);
    }, 6000);
    return () => clearInterval(id);
  }, [reduced, n]);

  const cur = slides[slide] ?? slides[0];
  if (!cur) return null; // empty slides (all rows deleted in the editor) → render nothing

  return (
    <>
      <div
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
        style={{ position: 'relative', marginTop: 40 }}
      >
        <div
          style={{
            overflow: 'hidden',
            borderRadius: 18,
            border: '1px solid var(--line)',
            background: '#0e0a05',
            boxShadow: '0 26px 60px rgba(0,0,0,.5)',
            aspectRatio: '3 / 2',
          }}
        >
          <div
            className="asta-track"
            style={{
              display: 'flex',
              transform: `translateX(-${slide * 100}%)`,
              ...(reduced ? { transition: 'none' } : null),
            }}
          >
            {slides.map((sl, i) => (
              <div key={`${sl.src}-${i}`} style={{ flex: '0 0 100%', minWidth: 0 }}>
                <img
                  src={sl.src}
                  alt={sl.title}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: fit,
                    padding: fit === 'contain' ? 18 : 0,
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={prev}
          aria-label="Previous slide"
          className="asta-cbtn"
          style={{
            position: 'absolute',
            top: '50%',
            left: 14,
            transform: 'translateY(-50%)',
            width: 46,
            height: 46,
            borderRadius: '50%',
            border: '1px solid var(--line)',
            background: 'rgba(14,10,5,.72)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: 'var(--gold-l)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeft size={22} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Next slide"
          className="asta-cbtn"
          style={{
            position: 'absolute',
            top: '50%',
            right: 14,
            transform: 'translateY(-50%)',
            width: 46,
            height: 46,
            borderRadius: '50%',
            border: '1px solid var(--line)',
            background: 'rgba(14,10,5,.72)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: 'var(--gold-l)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronRight size={22} strokeWidth={2.4} />
        </button>
      </div>

      {/* Caption + open-full + dots */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginTop: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: 'var(--gold)',
            }}
          >
            {cur.tag}
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 19, color: 'var(--cream)', marginTop: 2 }}>
            {cur.title}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
          <a
            href={cur.src}
            target="_blank"
            rel="noopener"
            className="asta-openfull"
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: 12.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--gold-l)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            Open full image <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {slides.map((sl, i) => (
              <button
                key={`${sl.src}-${i}`}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  width: i === slide ? 26 : 8,
                  height: 8,
                  padding: 0,
                  border: 0,
                  borderRadius: 999,
                  background: i === slide ? 'var(--gold)' : 'rgba(255,255,255,.28)',
                  cursor: 'pointer',
                  transition: 'width .3s, background .3s',
                  display: 'block',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
